<template>
  <v-navigation-drawer
    v-if="isTilelyticsEnabled"
    app
    floating
    :expand-on-hover="!$vuetify.breakpoint.mobile"
    :value="value"
    :mini-variant="!isMobile"
    overlay-color="var(--color-overlayBackground)"
    @input="$emit('input', $event)"
    class="wxapp-main-navigation"
  >
    <v-btn
      v-if="!$vuetify.breakpoint.mobile"
      :to="getCompanyOverviewRoute"
      exact
      depressed
      plain
      color="primary"
      class="wxapp-logo--drawer"
      accesskey="t"
      :title="$t('mainNav.logoAltText')"
    />

    <v-list nav dense flat>
      <!--
        Overview
        -->
      <v-list-item :to="getCompanyOverviewRoute">
        <v-list-item-icon>
          <v-icon>mdi-home-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t("mainNav.companyOverview") }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!--
        Performance Manager link
        -->
      <v-list-item
        v-if="showInsights"
        :to="getOpportunityExplorerRoute"
        :class="{ 'v-list-item--active': isActivePerformanceManagerUrl() }"
      >
        <v-list-item-icon>
          <v-icon>$insightsIcon</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t("mainNav.dpm") }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!--
        Factory's accordion sub-links (repeats for each)
        -->
      <v-list-group
        v-for="(factory, index) in factories"
        :key="`f-${index}`"
        :class="{ 'custom-group-active': isActiveFactory(factory.id) }"
        no-action
        eager
        dense
      >
        <template v-slot:activator>
          <v-list-item-icon>
            <v-icon>$factoryIcon</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ factory.name }}</v-list-item-title>
          </v-list-item-content>
        </template>

        <v-list-item
          v-for="(item, factoryIndex) in factoryMenuItems(factory.id)"
          @click="setFactory(factory)"
          :class="{ 'v-list-item--active': isActiveFactory(factory.id) }"
          :key="`f-${index}-${factoryIndex}`"
          :to="item.to"
          exact
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>

      <!--
        Admin accordion sub-links
        -->
      <v-list-group v-if="isAdmin" no-action eager :class="{ 'custom-group-active': isAdminUrl }">
        <template v-slot:activator>
          <v-list-item-icon>
            <v-icon>mdi-account-cog-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t("mainNav.admin") }}</v-list-item-title>
          </v-list-item-content>
        </template>
        <v-list-item v-for="(item, index) in navAdminItems" :key="`navAdminItem-${index}`" :to="item.to">
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-group>
    </v-list>

    <section v-if="isStyleNotesVisible">
      <a
        id="styleguide-breakpoint-label"
        target="confluence"
        href="https://worximity.atlassian.net/l/c/tbbudqAr"
        title="Responsive Breakpoint"
      />
      <!--
        Floating Action Button: Theme toggle switch
        -->
      <v-fab-transition>
        <v-btn
          id="themeToggleFab"
          color="black"
          elevation="10"
          accesskey="c"
          dark
          fab
          x-small
          fixed
          bottom
          left
          :title="this.$t('settings.dark')"
          v-if="!this.$vuetify.theme.dark"
          @click="setTheme('dark')"
        >
          <v-icon color="white">mdi-moon-waning-crescent</v-icon>
        </v-btn>
        <v-btn
          id="themeToggleFab"
          color="white"
          elevation="10"
          accesskey="c"
          light
          fab
          x-small
          fixed
          bottom
          left
          :title="this.$t('settings.light')"
          v-if="this.$vuetify.theme.dark"
          @click="setTheme('light')"
        >
          <v-icon color="black">mdi-white-balance-sunny</v-icon>
        </v-btn>
      </v-fab-transition>
    </section>
  </v-navigation-drawer>
</template>

<script>
import RouteService from "../router/RouteService";
import { setThemeToLocalStorage } from "@/components/Theme";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "MainNavigation",
  props: {
    value: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      accountName: "",
    };
  },
  watch: {
    account() {
      if (this.account) {
        this.accountName = this.account.name;
      }
    },
  },
  computed: {
    ...mapGetters("user", [
      "isAdmin",
      "isSupervisor",
      "factories",
      "isTilelyticsEnabled",
      "isWxUser",
      "insightsEnabled",
    ]),
    ...mapGetters("accounts", ["account", "accountId"]),
    ...mapGetters("navigation", ["activeFactory"]),
    getCompanyOverviewRoute() {
      return RouteService.toCompanyOverview();
    },
    getOpportunityExplorerRoute() {
      return RouteService.toShiftChanges(); // For DPM R1 nav redirects to this page
    },
    currentUrl() {
      return this.$route.path;
    },
    isActiveFactoryUrl() {
      return (
        this.currentUrl.includes("dashboard") ||
        this.currentUrl.includes("availability-downtime") ||
        this.currentUrl.includes("production-data-export")
      );
    },
    isAdminUrl() {
      return this.currentUrl.includes("factory-settings");
    },
    navAdminItems() {
      return [
        {
          title: this.$t("mainNav.customPeriods"),
          to: RouteService.toCustomPeriods(),
        },
      ];
    },
    showInsights() {
      return this.insightsEnabled || this.isWxUser;
    },
    isStyleNotesVisible() {
      return process.env.VUE_APP_GUIDE_VISIBLE === "true";
    },
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
  },
  methods: {
    ...mapActions("navigation", ["setActiveFactory"]),
    ...mapActions("user", ["updateTheme"]),
    setFactory(factory) {
      this.setActiveFactory(factory);
    },
    isActiveFactory(factory) {
      return this.activeFactory === factory && this.isActiveFactoryUrl;
    },
    isActivePerformanceManagerUrl() {
      return this.currentUrl.includes("shift-changes") || this.currentUrl.includes("line-starts");
    },
    factoryMenuItems(factoryId) {
      let factoryOverviewMenuItem = {
        title: this.$t("mainNav.dashboard"),
        to: RouteService.toDashboard(factoryId),
        icon: "mdi-chart-donut",
      };
      let factoryAvailabilityMenuItem = {
        title: this.$t("mainNav.sf1Availability"),
        to: RouteService.toAvailabilityAndDowntime(factoryId),
        icon: "$availabilityDowntimesIcon",
      };
      let factoryDataExportMenuItem = {
        title: this.$t("mainNav.productionDataExport"),
        to: RouteService.toProductionDataExportRoute(factoryId),
        icon: "mdi-cloud-download-outline",
      };
      let factoryEmailReportsMenuItem = {
        title: this.$t("mainNav.myReports"),
        to: RouteService.toMyReportsRoute(factoryId),
        icon: "mdi-email",
      };
      return [
        factoryOverviewMenuItem,
        factoryAvailabilityMenuItem,
        factoryDataExportMenuItem,
        factoryEmailReportsMenuItem,
      ];
    },
    setTheme(newTheme) {
      this.$vuetify.theme.dark = newTheme === "dark";
      setThemeToLocalStorage(this.$vuetify.theme.dark);
      this.updateTheme(newTheme);
    },
  },
};
</script>

<style lang="scss" scoped>
// local var
$boxShadow_color: hsl(216, 91%, 4%);

// style
.wxapp-main-navigation {
  &.v-navigation-drawer {
    padding-top: var(--v-application-top);
    background: var(--color-element-layer1);

    &.theme--dark {
      box-shadow: 0px 6px 12px rgba($boxShadow_color, 0.2);
    }
    &.theme--light {
      box-shadow: 0px 1px 2px rgba($boxShadow_color, 0.3), 0px 1px 3px 1px rgba($boxShadow_color, 0.15);
    }

    // !isMobile
    @media ($wx-md-min) {
      padding-top: 7px; // differs from TilePlus because of borders within the logo img
    }
    @media ($wx-xl-min) {
      padding-top: 16px; // vertically align logo with main-title
    }
  }

  // manage vertical spaces within accordions
  ::v-deep .v-list-group {
    &__items {
      .v-list-item {
        margin-bottom: 4px;
      }
    }
    // last link of the last accordion
    &:last-child {
      .v-list-group__items {
        .v-list-item {
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  // hack Vuetify to highlight the active page.
  // this generates UX confusion when an accordion is open.
  .v-list-item--active {
    color: var(--color-primary);

    &:before {
      opacity: 0;
    }
  }
}

// Group + Active
::v-deep .v-list-group--active ::v-deep .v-list-group__header.v-list-item--active {
  color: var(--color-text-theme);
}

// Custom + Group + Active
::v-deep .custom-group-active .v-list-group__header.v-list-item,
::v-deep .custom-group-active .v-list-group__header.v-list-item .v-icon {
  color: var(--color-primary) !important;
}

.wxapp-logo--drawer.v-btn {
  // local vars
  $logo_iconSize: 36px;
  $logo_padding: 10px;
  // style
  height: ($logo_iconSize + $logo_padding);
  width: ($logo_iconSize + $logo_padding);
  margin-top: 3px;
  margin-bottom: 12px;
  margin-left: 3px;
  padding: $logo_padding;
  border-radius: $logo_padding;
  background: url("~@/assets/Tilelytics-Logo-Square-Light.svg") center center no-repeat;
  background-size: $logo_iconSize;

  // &.theme--light {
  //   background-image: url("~@/assets/Tilelytics-Logo-Square-Light.svg");
  // }

  &:not(:hover):not(:focus)::before {
    opacity: 0;
  }
}

/*
 * These 2 elements are for designer’s QA.
 * It should NEVER be visible on PROD server
 */
#themeToggleFab {
  left: 12px;
  transition: var(--smooth-transition);
}
#styleguide-breakpoint-label {
  display: block;
  position: absolute;
  left: 8px;
  bottom: 56px;
  height: 2rem;
  max-width: 2.5rem;
  font-style: normal;
  color: var(--color-text-subtle-theme);
  border-radius: var(--border-radius-sm);
  transition: var(--smooth-transition);
  &:hover {
    color: var(--color-text-theme);
    cursor: help;
  }
  &:before,
  &:after {
    position: absolute;
    padding-left: 0.5rem;
    min-width: 2rem;
    text-align: center;
    line-height: 1;
  }
  // responsive
  &:before {
    top: -5px;
    font-size: 24px;
    content: "\2194";
  }
  &:after {
    top: 14px;
    font-size: 14px;
    font-weight: bold;
    content: "XS";
    @media ($wx-sm-min) {
      content: "SM";
    }
    @media ($wx-md-min) {
      content: "MD";
    }
    @media ($wx-lg-min) {
      content: "LG";
    }
    @media ($wx-xl-min) {
      content: "XL";
    }
  }
}
</style>
